// src/components/ListComponent.js
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiXCircle } from "react-icons/fi";

import ListRowItem from "./ListRowItem";
import "./ListComponent.css";

export default function ListComponent({
  title = "List",

  // ✅ NEW: type of list (for later styling/behavior)
  itemType = "exercise",

  // data
  items = [],
  groups = [],

  // filtering (optional)
  enableGroupFilter = false,
  filterLabel = "All",
  filterTitle = "Filter",
  groupForItem, // (item) => string groupName

  // controlled/uncontrolled filter
  filterIdx: controlledFilterIdx,
  onFilterIdxChange,

  // sorting
  sortItems, // optional custom sorter: (items) => items

  // marked
  markedIds = new Set(),
  onToggleMarked,
  markedClassName = "done",
  markTitle = (isMarked) => (isMarked ? "Unmark" : "Mark"),
  MarkIcon,

  // favorites
  favoriteIds = new Set(),
  onToggleFavorite,
  showFavorites = false,
  FavIcon,
  FavIconFilled,

  // row rendering
  getId = (x) => x?.id,
  getTitle = (x) => x?.name ?? "",
  subtitleForItem, // (x) => string
  onItemClick,

  density = "normal",
}) {
  const isControlled =
    typeof controlledFilterIdx === "number" && typeof onFilterIdxChange === "function";
  const [uncontrolledIdx, setUncontrolledIdx] = useState(0);
  const filterIdx = isControlled ? controlledFilterIdx : uncontrolledIdx;
  const setFilterIdx = isControlled ? onFilterIdxChange : setUncontrolledIdx;

  const filterOptions = useMemo(() => {
    if (!enableGroupFilter) return [filterLabel];
    return [filterLabel, ...(groups || [])];
  }, [enableGroupFilter, filterLabel, groups]);

  useEffect(() => {
    if (filterIdx >= filterOptions.length) setFilterIdx(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions.length]);

  const activeFilter = filterOptions[filterIdx] || filterLabel;

  const filtered = useMemo(() => {
    if (!enableGroupFilter) return items;
    if (activeFilter === filterLabel) return items;
    if (typeof groupForItem !== "function") return items;

    return items.filter((x) => String(groupForItem(x) || "").trim() === activeFilter);
  }, [items, enableGroupFilter, activeFilter, filterLabel, groupForItem]);

  const sorted = useMemo(() => {
    if (typeof sortItems === "function") return sortItems(filtered);

    const collator = new Intl.Collator(undefined, { sensitivity: "base" });
    return [...filtered].sort((a, b) => {
      const aId = String(getId(a));
      const bId = String(getId(b));
      const aMarked = markedIds?.has?.(aId);
      const bMarked = markedIds?.has?.(bId);
      if (aMarked && !bMarked) return -1;
      if (!aMarked && bMarked) return 1;
      return collator.compare(String(getTitle(a) || ""), String(getTitle(b) || ""));
    });
  }, [filtered, sortItems, markedIds, getId, getTitle]);

  const cycleFilter = () => {
    if (filterOptions.length <= 1) return;
    setFilterIdx((i) => (i + 1) % filterOptions.length);
  };

  const clearFilter = () => setFilterIdx(0);

  return (
    <div className={`wl ${density === "compact" ? "wl--compact" : ""}`}>
      <div className="panel panel--stretch glass">
        <div className="header-row">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h3 style={{ margin: 0 }}>{title}</h3>

            {enableGroupFilter ? (
              <>
                <button
                  type="button"
                  className="chip chip--hdr"
                  onClick={cycleFilter}
                  disabled={filterOptions.length <= 1}
                  title={filterTitle}
                >
                  {activeFilter}
                </button>

                <button
                  type="button"
                  className="chip chip--hdr chip--hdr-icon"
                  onClick={clearFilter}
                  disabled={activeFilter === filterLabel}
                  title={activeFilter === filterLabel ? "No filter active" : "Clear filter"}
                  aria-label="Clear filter"
                >
                  <FiXCircle size={22} />
                </button>
              </>
            ) : null}
          </div>
        </div>

        <motion.div className="list scroll" layout>
          <AnimatePresence initial={false}>
            {sorted.map((x) => {
              const id = String(getId(x));
              const isMarked = Boolean(markedIds?.has?.(id));
              const isFav = Boolean(favoriteIds?.has?.(id));
              const subtitle =
                typeof subtitleForItem === "function" ? subtitleForItem(x) : "";

              return (
                <ListRowItem
                  key={id}
                  itemType={itemType}
                  x={x}
                  id={id}
                  isMarked={isMarked}
                  isFav={isFav}
                  title={getTitle(x)}
                  subtitle={subtitle}
                  onItemClick={onItemClick}
                  onToggleMarked={onToggleMarked}
                  onToggleFavorite={onToggleFavorite}
                  markedClassName={markedClassName}
                  markTitle={markTitle}
                  MarkIcon={MarkIcon}
                  showFavorites={showFavorites}
                  FavIcon={FavIcon}
                  FavIconFilled={FavIconFilled}
                />
              );
            })}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}